/**
 * Cookie consent state.
 *
 * Three buckets are stored explicitly:
 *   - marketing: Google AdSense / advertising cookies
 *   - personalization: using browsing data to personalize ads
 *   - analytics: Google Analytics 4 / usage measurement cookies
 *
 * Strictly necessary cookies are always on and are not stored as a choice.
 * No advertising or analytics script is injected and no ad slot renders until
 * the corresponding consent bucket is explicitly granted.
 */

export const CONSENT_STORAGE_KEY = "easements.cookie-consent.v1";
export const CONSENT_CHANGE_EVENT = "easements:cookie-consent-change";

export type ConsentDecision = {
  /** True when the visitor accepted marketing/advertising cookies. */
  marketing: boolean;
  /** True when the visitor accepted personalized advertising. */
  personalization: boolean;
  /** True when the visitor accepted analytics/usage measurement cookies. */
  analytics: boolean;
  /** ISO timestamp of the decision, for audit and re-prompt logic. */
  decidedAt: string;
};

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function migrateLegacyDecision(parsed: Partial<ConsentDecision>): ConsentDecision | null {
  if (typeof parsed?.marketing !== "boolean") return null;
  return {
    marketing: parsed.marketing,
    // Older decisions only stored `marketing`. Treat an old "accept" as both
    // marketing + personalization granted so behavior does not regress.
    personalization: parsed.personalization ?? parsed.marketing,
    // Older decisions did not store analytics. Default to false so the visitor
    // is not opted into GA4 without an explicit choice.
    analytics: parsed.analytics ?? false,
    decidedAt: typeof parsed.decidedAt === "string" ? parsed.decidedAt : new Date().toISOString(),
  };
}

/** The stored decision, or null when the visitor has not chosen yet. */
export function getConsent(): ConsentDecision | null {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentDecision>;
    return migrateLegacyDecision(parsed);
  } catch {
    return null;
  }
}

/** True only when the visitor actively granted marketing consent. */
export function hasMarketingConsent(): boolean {
  return getConsent()?.marketing === true;
}

/** True only when the visitor actively granted personalization consent. */
export function hasPersonalizationConsent(): boolean {
  return getConsent()?.personalization === true;
}

/** True only when the visitor actively granted analytics consent. */
export function hasAnalyticsConsent(): boolean {
  return getConsent()?.analytics === true;
}

/** Records a decision and notifies every listener in this tab. */
export function setConsent(decision: Omit<ConsentDecision, "decidedAt">): ConsentDecision {
  const full: ConsentDecision = { ...decision, decidedAt: new Date().toISOString() };
  if (isBrowser()) {
    try {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(full));
    } catch {
      // Private mode / storage disabled: the decision only lasts this page view.
    }
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail: full }));
  }
  return full;
}

/** Clears the decision so the banner shows again (used by the privacy/cookie pages). */
export function resetConsent(): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // ignore
  }
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail: null }));
}

/** Subscribes to consent changes in this tab and other tabs. */
export function subscribeConsent(listener: () => void): () => void {
  if (!isBrowser()) return () => {};
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === CONSENT_STORAGE_KEY) listener();
  };
  window.addEventListener(CONSENT_CHANGE_EVENT, listener);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(CONSENT_CHANGE_EVENT, listener);
    window.removeEventListener("storage", onStorage);
  };
}
